import { useCallback, useMemo } from "react";

import * as audienceApi from "@/api";
import { useAudienceActions } from "@/context";
import { useAudienceCache, useClient, useServices } from "@/hooks";
import { Audience } from "@/types";
import { audienceListPath, unwrap } from "@/utils";
import { useAudienceTask } from "./useAudienceTask";

type AudienceTask = ReturnType<typeof useAudienceTask>;

/**
 * Changes to a saved audience: rename, delete, share, unshare and clone.
 * Pass a `task` to share its action state, e.g. with the rest of a page,
 * otherwise the mutations get their own.
 */
export const useAudienceMutations = (audience?: Audience, task?: AudienceTask) => {
  const { nav, ui } = useServices();
  const audienceActions = useAudienceActions();
  const client = useClient();
  const cache = useAudienceCache();
  const ownTask = useAudienceTask();
  const { action, run } = task ?? ownTask;

  const rename = useCallback(async (audienceName: string, projectName: string | null) => {
    if (!audience) return;
    const audienceId = audience.audience_id;

    await run('update', 'Updating Audience...', async ({ token }) => {
      const response = await audienceApi.updateAudienceName(token, audienceId, audienceName, projectName);
      ui.closeDialog();
      unwrap(response);

      cache.rename(audienceId, audienceName, projectName);
      audienceActions.renamed(audienceId, audienceName, projectName);
    }, { success: "Audience name updated successfully", notifyError: true });
  }, [audience, cache, audienceActions, run, ui]);

  const remove = useCallback(async (redirect?: string) => {
    if (!audience) return;

    await run('delete', 'Deleting Audience...', async ({ token }) => {
      unwrap(await audienceApi.deleteAudience(
        token,
        audience.audience_name,
        audience.audience_id,
        audience.audience_type,
        client,
      ));

      cache.remove(audience.audience_id);
      ui.closeDialog();
      redirect && nav.navigate(redirect);
    }, { success: "Audience deleted successfully", notifyError: true });
  }, [audience, cache, client, nav, run, ui]);

  const share = useCallback(async (ids: number[]) => {
    if (!audience) return;

    await run('share', 'Sharing Audience...', async ({ token }) => {
      const response = await audienceApi.shareAudience(token, audience.audience_id, !!audience.shared, ids);
      ui.closeDialog();
      unwrap(response);

      cache.share(audience.audience_id, ids);
      audienceActions.shared(audience.audience_id, ids);
    }, { success: "Audience shared successfully", notifyError: true });
  }, [audience, cache, audienceActions, run, ui]);

  // Removes an audience shared with the user, then returns to its list
  const unshare = useCallback(async () => {
    if (!audience) return;

    await run('delete', 'Removing Shared Audience...', async ({ token }) => {
      unwrap(await audienceApi.deleteShare(token, audience.audience_id));

      cache.remove(audience.audience_id);
      ui.closeDialog();
      nav.navigate(audienceListPath(audience));
    }, { success: "Shared Audience removed successfully", notifyError: true });
  }, [audience, cache, nav, run, ui]);

  const clone = useCallback(async () => {
    if (!audience) return;

    await run('clone', 'Cloning Audience...', async ({ token }) => {
      const response = await audienceApi.cloneAudience(token, audience.audience_id, client);
      ui.closeDialog();
      unwrap(response);

      cache.saved();
    }, { success: "Audience cloned successfully", notifyError: true });
  }, [audience, cache, client, run, ui]);

  return useMemo(
    () => ({ rename, remove, share, unshare, clone, action }),
    [rename, remove, share, unshare, clone, action]
  );
}

export type AudienceMutations = ReturnType<typeof useAudienceMutations>;
